// Load the editor dynamically to avoid bundling quill-related CJS code into main chunk
import katex from 'katex'
import { repairMathControlChars } from '../../pages/Exams/generate/MathText'
import 'katex/dist/katex.min.css'
import renderMathInElement from 'katex/contrib/auto-render'
import { useEffect, useReducer, useRef } from 'react'
import { ArrowLeft, ArrowRight, ImagePlus, Plus, Trash2 } from 'lucide-react'
import { ConfirmDialog, IconButton, Pager } from '../../school_panel/components/ui'
import React, { useState } from "react";
import DOMPurify from 'dompurify';
import { IQuestion } from '../../types/question';

const PAGE_SIZE = 10
const LETTERS = ['A', 'B', 'C', 'D'] as const
const CHOICE_FIELDS = ['choice_a', 'choice_b', 'choice_c', 'choice_d'] as const

// Sanitize, then render $$display$$ and $inline$ math with KaTeX.
const mathHtml = (raw: string | undefined) => {
    try {
        const s = DOMPurify.sanitize(raw || '')
        return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(repairMathControlChars(expr), { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(repairMathControlChars(expr), { displayMode: false, throwOnError: false }) } catch (e) { return _m } })
    } catch (e) { return DOMPurify.sanitize(String(raw || '')) }
}
const plain = (html: string | undefined) => String(html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim()

/** True when a question has explanation text (or an image-only explanation). */
export const hasExplanation = (q: any) => plain(q?.explanation) !== '' || /<img/i.test(String(q?.explanation || ''))
/** Manually added questions store the key as `ans`; extracted ones as `answer`. */
export const answerKeyOf = (q: any): string => String(q?.answer ?? q?.ans ?? '').trim().toUpperCase()

const CHIP = 'flex-none whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold'
const SOURCE_LABEL: Record<string, string> = { key: 'Answer from key', printed: 'Answer printed in file', ai: 'Answer by AI' }

// Compress an image File to target size (kb) using canvas. Returns a File.
const compressImageFile = async (file: File, maxKB = 40): Promise<File> => {
    const maxBytes = maxKB * 1024
    if (file.size <= maxBytes) return file

    const img = document.createElement('img')
    const objectUrl = URL.createObjectURL(file)
    img.src = objectUrl

    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('Failed to load image for compression'))
    })

    const width = img.naturalWidth
    const height = img.naturalHeight
    const toBlob = (quality: number, mime = 'image/jpeg', w = width, h = height) => {
        return new Promise<Blob | null>((res) => {
            const c = document.createElement('canvas')
            c.width = w
            c.height = h
            const cctx = c.getContext('2d')!
            cctx.drawImage(img, 0, 0, w, h)
            c.toBlob((b) => res(b), mime, quality)
        })
    }

    let quality = 0.92
    let attempt = 0
    let blob: Blob | null = null
    let curW = width
    let curH = height

    while (attempt < 12) {
        blob = await toBlob(quality, 'image/jpeg', curW, curH)
        if (blob && blob.size <= maxBytes) break
        quality = Math.max(0.25, quality - 0.12)
        attempt++
        if (attempt % 4 === 0) {
            curW = Math.max(200, Math.floor(curW * 0.85))
            curH = Math.max(200, Math.floor(curH * 0.85))
        }
    }

    URL.revokeObjectURL(objectUrl)
    if (blob) {
        try {
            return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })
        } catch (e) {
            return new File([blob], file.name, { type: blob.type })
        }
    }
    return file
}

// Simple fallback editor used when the rich editor cannot be loaded.
const SimpleFallbackEditor = ({ initialValue, onChange }: { initialValue?: string, onChange?: (val: string) => void }) => {
    const [val, setVal] = useState<string>(initialValue || '');
    useEffect(() => { setVal(initialValue || '') }, [initialValue]);
    return (
        <textarea
            value={val}
            onChange={(e) => { setVal(e.target.value); onChange && onChange(e.target.value) }}
            className="min-h-[96px] w-full rounded-[12px] border border-line-strong bg-surface p-3 text-sm leading-normal text-ink focus:outline-2 focus:outline-offset-1 focus:outline-accent" />
    )
}

const LABEL = 'mb-1.5 block text-[13px] font-semibold text-ink'

/**
 * Design ExamEditor step 2: the question list (4 columns) beside the question
 * editor (8 columns). `panel` replaces the editor (the import panel); `footer`
 * sits under the list (the import pills); `onAdd` is the header "Add" button.
 */
const ExamQuestionsInitial = ({ questions, onQuestionsChange, onEdit, onAdd, panel, footer }: {
    questions: IQuestion[]
    /** Kept for callers that still pass it; saving happens in the wizard. */
    onProceedToMeta?: (qs: IQuestion[]) => void
    /** Called with a new array after a question is deleted. */
    onQuestionsChange?: (qs: IQuestion[]) => void
    /** Called after a field of a question was edited in place. */
    onEdit?: () => void
    onAdd?: () => void
    panel?: React.ReactNode
    footer?: React.ReactNode
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [, bump] = useReducer((n: number) => n + 1, 0)
    const [sel, setSel] = useState(0)
    const [page, setPage] = useState(1)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [EditorComp, setEditorComp] = useState<any>(null);

    const count = questions.length
    const cur = Math.min(sel, Math.max(0, count - 1))
    const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE))
    const curPage = Math.min(page, pageCount)
    const q = (questions[cur] ?? null) as any
    const missing = questions.filter((x) => !hasExplanation(x)).length

    const select = (i: number) => { setSel(i); setPage(Math.floor(i / PAGE_SIZE) + 1) }

    useEffect(() => {
        // After questions render, run KaTeX auto-render to catch any inline/display math we didn't replace
        try {
            if (containerRef.current) {
                renderMathInElement(containerRef.current, {
                    delimiters: [
                        { left: '$$', right: '$$', display: true },
                        { left: '$', right: '$', display: false },
                        { left: '\\(', right: '\\)', display: false },
                        { left: '\\[', right: '\\]', display: true }
                    ],
                    throwOnError: false,
                })
            }
        } catch (e) {
            // ignore auto-render errors
        }
    }, [questions, curPage])

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const mod = await import('quill-editor-math');
                if (!mounted) return;
                const Comp = (mod && (mod.default || mod));
                setEditorComp(() => Comp);
                try { await import('quill-editor-math/dist/index.css'); } catch (e) { }
            } catch (e) {
                // If dynamic import fails (commonly in production builds due to bundling/CJS issues),
                // fall back to the simple textarea editor so editing still works.
                if (!mounted) return
                setEditorComp(() => SimpleFallbackEditor)
            }
        })();
        return () => { mounted = false; };
    }, [])

    const setField = (field: string, value: any) => {
        if (!q) return
        q[field] = value
        bump()
        onEdit?.()
    }
    const setAnswer = (letter: string) => {
        if (!q) return
        q.answer = letter
        if ('ans' in q) q.ans = letter
        bump()
        onEdit?.()
    }
    const remove = () => {
        if (!q) return
        const next = questions.filter((_, i) => i !== cur)
        setSel(Math.max(0, cur - 1))
        if (onQuestionsChange) onQuestionsChange(next)
        else { questions.splice(cur, 1); bump() }
    }
    const attachImage = async (f: File | null) => {
        if (!q) return
        if (!f) { delete q.pendingImageFile; delete q.pendingImagePreview; bump(); return }
        let file = f
        try { file = await compressImageFile(f, 40) } catch { /* fall back to the original file */ }
        const url = URL.createObjectURL(file)
        q.pendingImageFile = file
        q.pendingImagePreview = url
        q.image = url
        bump()
    }

    const rows = questions.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE).map((element, k) => {
        const i = (curPage - 1) * PAGE_SIZE + k
        const el = element as any
        const chapter = String(el.chapter_name || el.chapter || '').trim()
        const difficulty = String(el.difficulty || '').trim()
        const difficultyLabel = difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase() : ''
        const on = i === cur
        return (
            <li key={i}>
                <button type="button" aria-current={on ? 'true' : undefined} onClick={() => select(i)}
                    className={`relative flex w-full items-start gap-3 overflow-hidden rounded-[14px] border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? 'border-line-strong bg-ground before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-accent' : 'border-transparent bg-transparent hover:bg-surface-2'}`}>
                    <span className={`grid h-[30px] w-[30px] flex-none place-items-center rounded-[10px] font-mono text-xs font-semibold ${on ? 'bg-navy text-white' : 'bg-track text-muted-2'}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 block break-words font-medium text-ink" dangerouslySetInnerHTML={{ __html: mathHtml(element.question) || 'Untitled question' }} />
                        {(chapter || difficultyLabel) && (
                            <span className="mt-1 flex flex-wrap gap-x-2 text-xs text-muted">
                                {chapter && <span>{chapter}</span>}
                                {chapter && difficultyLabel && <span aria-hidden>·</span>}
                                {difficultyLabel && <span>{difficultyLabel}</span>}
                            </span>
                        )}
                    </span>
                    <span className="flex flex-none flex-col items-end gap-1">
                        {answerKeyOf(element) === '' && <span title="No correct answer set" className={`${CHIP} bg-warning-bg text-warning-ink`}>No answer</span>}
                        {el.needs_check && <span title="Extraction was uncertain, check this question" className={`${CHIP} bg-warning-bg text-warning-ink`}>Check</span>}
                        {el.answer_source === 'ai' && <span title="The answer was suggested by AI, please verify" className={`${CHIP} bg-mist text-slate`}>AI answer</span>}
                        {el.has_figure && <span title="The question refers to a figure" className={`${CHIP} bg-mist text-slate`}>Figure</span>}
                        {!hasExplanation(element) && (
                            <span title="Missing explanation" className={`${CHIP} bg-warning-bg text-warning-ink`}>No expl.</span>
                        )}
                    </span>
                </button>
            </li>
        )
    })

    const answer = answerKeyOf(q)
    const srcLabel = Object.hasOwn(SOURCE_LABEL, q?.answer_source) ? SOURCE_LABEL[q.answer_source] : ""
    const preview = q?.pendingImagePreview || q?.image || null

    return (
        <div ref={containerRef} className="grid grid-cols-1 gap-[18px] lg:grid-cols-12">
            <ConfirmDialog isOpen={confirmDelete} title={`Delete question ${cur + 1}?`} body="It is removed from this list. You can add it again by importing." confirmLabel="Delete"
                onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); remove() }} />
            <section aria-labelledby="ql-title" className="min-w-0 rounded-[20px] border border-line bg-surface p-4 shadow-card lg:col-span-4">
                <div className="mx-1.5 mb-3 mt-1 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <h2 id="ql-title" className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Questions <span className="font-mono text-sm font-medium text-muted">{count}</span></h2>
                        <p className="mt-0.5 text-xs text-muted">{count === 0 ? 'Nothing added yet' : missing ? `${missing} missing an explanation` : 'All complete'}</p>
                    </div>
                    {onAdd && (
                        <button type="button" onClick={onAdd}
                            className="inline-flex min-h-[38px] flex-none items-center gap-2 rounded-[12px] border border-navy bg-navy px-3 text-sm font-semibold text-white hover:bg-navy-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            <Plus size={18} strokeWidth={1.8} aria-hidden />Add
                        </button>
                    )}
                </div>
                {count > 0 && <ol className="flex flex-col gap-0.5">{rows}</ol>}
                {pageCount > 1 && <Pager className="mt-3 px-1.5" page={curPage} pageCount={pageCount} onPage={setPage} label="Questions pagination" />}
                {footer && <div className="mt-2.5 border-t border-hairline px-1.5 pb-1 pt-3.5">{footer}</div>}
            </section>

            <section aria-labelledby="qe-title" className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card lg:col-span-8">
                {panel ? panel : !q ? (
                    <p id="qe-title" className="text-sm text-muted">Add a question or import a file to start editing.</p>
                ) : (
                    <>
                        <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Question {cur + 1} of {count}</p>
                                <h2 id="qe-title" className="mt-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Edit question</h2>
                                {(q.page || srcLabel) && (
                                    <p className="mt-1.5 flex flex-wrap gap-1.5">
                                        {q.page ? <span className={`${CHIP} bg-mist text-slate`}>From page {q.page}</span> : null}
                                        {srcLabel && <span className={`${CHIP} bg-mist text-slate`}>{srcLabel}</span>}
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <IconButton size="lg" label="Previous question" disabled={cur <= 0} onClick={() => select(Math.max(0, cur - 1))} icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />} />
                                <IconButton size="lg" label="Next question" disabled={cur >= count - 1} onClick={() => select(Math.min(count - 1, cur + 1))} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} />
                                <IconButton size="lg" tone="danger" label="Delete question" onClick={() => setConfirmDelete(true)} icon={<Trash2 size={18} strokeWidth={1.8} aria-hidden />} className="!text-danger-ink" />
                            </div>
                        </div>

                        <div key={cur}>
                            <p className={LABEL}>Question</p>
                            {EditorComp
                                ? <EditorComp initialValue={q.question} onChange={(v: any) => setField('question', v)} />
                                : <div className="text-sm text-muted">Loading editor…</div>}
                            {preview ? <img src={String(preview)} alt={`Question ${cur + 1} attachment`} className="mt-2 max-h-40 rounded-[10px]" /> : null}
                            <div className="mb-5 mt-2 flex flex-wrap items-center justify-between gap-2">
                                <label className="inline-flex min-h-[34px] cursor-pointer items-center gap-1.5 rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink hover:border-slate focus-within:ring-2 focus-within:ring-accent">
                                    <ImagePlus size={16} strokeWidth={1.8} aria-hidden />Add image
                                    <input id="edit-question-image" title="Upload an image (will be compressed to <40KB)" type="file" accept="image/*" className="sr-only"
                                        onChange={(e) => { void attachImage(e.target.files ? e.target.files[0] : null) }} />
                                </label>
                                <span className="text-xs text-muted">Images are compressed to under 40 KB</span>
                            </div>

                            <p id="ans-lbl" className={LABEL}>Choices <span className="font-normal text-muted">· select the correct answer</span></p>
                            <div role="radiogroup" aria-labelledby="ans-lbl" className="flex flex-col gap-2">
                                {LETTERS.map((letter, k) => {
                                    const ok = answer === letter
                                    return (
                                        <div key={letter} className={`flex items-start gap-3 rounded-[14px] border py-1.5 pl-3 pr-2.5 ${ok ? 'border-success/40 bg-success-bg' : 'border-line-strong bg-surface'}`}>
                                            <button type="button" role="radio" aria-checked={ok} aria-label={`Mark choice ${letter} as correct`} onClick={() => setAnswer(letter)}
                                                className={`mt-2.5 h-[22px] w-[22px] flex-none rounded-full border-2 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${ok ? 'border-success bg-success shadow-[inset_0_0_0_4px_rgb(var(--yd-surface))]' : 'border-control bg-surface hover:border-success'}`} />
                                            <span aria-hidden className={`mt-2 grid h-7 w-7 flex-none place-items-center rounded-[9px] font-mono text-[13px] font-semibold ${ok ? 'bg-success text-white' : 'bg-track text-muted-2'}`}>{letter}</span>
                                            <div className="min-w-0 flex-1 py-0.5">
                                                <span className="sr-only">Choice {letter}</span>
                                                {EditorComp
                                                    ? <EditorComp initialValue={q[CHOICE_FIELDS[k]]} onChange={(v: any) => setField(CHOICE_FIELDS[k], v)} />
                                                    : <div className="text-sm text-muted">Loading editor…</div>}
                                            </div>
                                            {ok && <span className="mt-2.5 inline-flex flex-none items-center rounded-full bg-success-bg px-2 py-[3px] font-mono text-xs font-semibold text-success">Correct</span>}
                                        </div>
                                    )
                                })}
                            </div>

                            <p className={`${LABEL} mt-5`}>Explanation <span className="font-normal text-muted">· shown to students after results are released</span></p>
                            {EditorComp
                                ? <EditorComp initialValue={q.explanation || ''} onChange={(v: any) => setField('explanation', v || '')} />
                                : <div className="text-sm text-muted">Loading editor…</div>}
                            {!hasExplanation(q) && <p className="mt-1.5 text-xs text-warning-ink">No explanation yet. Students learn more when they see why.</p>}

                            <p className={`${LABEL} mt-5`}>Note <span className="font-normal text-muted">(optional)</span></p>
                            {EditorComp
                                ? <EditorComp initialValue={q.note || ''} onChange={(v: any) => setField('note', v)} />
                                : <div className="text-sm text-muted">Loading editor…</div>}
                        </div>
                    </>
                )}
            </section>
        </div>
    )
}

export default ExamQuestionsInitial
